import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useAction, useQuery, useMutation, useConvexAuth } from 'convex/react';
import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';
import { t, type SupportedLanguage } from '@/i18n/index';
import { isFinalGateEligible } from './ValidationGateBadge';
import { HistoryItemReplay } from './HistoryItemReplay';
import { LAUNCH_TENANT_ID } from '../config/tenant';
import './Chat.css';
import './TypingIndicator.css';
import './HelpModal.css';
import bihFlagWatermark from '../../../Design/1.png';
import { stripDebugHeaders } from '../utils/chatGuards';
import { TypingIndicator } from './TypingIndicator';
import { HelpModal } from './HelpModal';
import { useAuthUser } from '../hooks/useAuth';
import {
  normalizeClassificationResult,
  type ClassificationResult as CanonicalClassificationResult,
} from "../types/classificationResult";
import { normalizeConfidenceScore } from "../utils/confidence";
import {
  buildPublicClassificationView,
  formatPublicClassificationText,
} from "../presentation/classificationPresentation";

const LOCALE_MAP: Record<string, string> = {
  bs: 'hr-HR', 'bs-Cyrl': 'hr-HR', sr: 'sr-RS', 'sr-Cyrl': 'sr-RS',
  en: 'en-US', de: 'de-DE', fr: 'fr-FR', sk: 'sk-SK', it: 'it-IT',
  mk: 'mk-MK', ru: 'ru-RU', es: 'es-ES',
};

interface Message {
  role: 'user' | 'assistant';
  content: string;
  isResult?: boolean;
  imagePreview?: string;
  classificationData?: MessageClassificationData;
}

/**
 * Chat-local superset of canonical ClassificationResult that includes
 * the user's product description (not a backend transport field).
 */
type MessageClassificationData = CanonicalClassificationResult & {
  description?: string;
};

type ChatRuntimePayload = Record<string, unknown> & {
  hs_code?: string | null;
};

function isChatRuntimePayload(raw: unknown): raw is ChatRuntimePayload {
  return Boolean(raw && typeof raw === "object");
}

function collectMissingFacts(result: CanonicalClassificationResult): string[] {
  const facts = new Set<string>();
  const missingProductFacts = result.validatorGate?.final_eligibility?.missing_product_facts;
  if (Array.isArray(missingProductFacts)) {
    for (const fact of missingProductFacts) {
      if (fact.trim()) facts.add(fact);
    }
  }
  if (result.missing_info?.trim()) facts.add(result.missing_info.trim());
  for (const question of result.suggested_questions ?? []) {
    if (question.trim()) facts.add(question.trim());
  }
  return [...facts];
}

function normalizeUserFacingProse(value: string): string {
  return value.toLocaleLowerCase().replace(/[\p{P}\p{S}\s]+/gu, " ").trim();
}

export function shouldRenderClassificationReasoning(result: CanonicalClassificationResult): boolean {
  const reasoning = result.reasoning?.trim();
  if (!reasoning) return false;
  const normalizedReasoning = normalizeUserFacingProse(reasoning);
  return !collectMissingFacts(result).some(
    (fact) => normalizeUserFacingProse(fact) === normalizedReasoning,
  );
}

/**
 * History item from classification_history table
 */
interface HistoryItem {
  _id: string;
  hs_code: string;
  description: string;
  confidence: string;
  confidence_score: number;
  gir_path?: string[];
  legal_basis?: string;
  reasoning?: string;
  status?: string;
  created_at: number;
  // V2.4: Link to audit-grade classification_cases for replay parity
  caseId?: Id<'classification_cases'>;
}

const PROGRESS_STEPS = [
  { delay: 0, message: '💬 Primam zahtjev...' },
  { delay: 2000, message: '🧠 Analiziram opis i identificiram ključne detalje...' },
  { delay: 5000, message: '🔍 Pretražujem HS kodove i pravila klasifikacije...' },
  { delay: 8000, message: '📚 Konsultujem carinsku tarifu i GIR pravila...' },
  { delay: 12000, message: '⚖️ Validiram klasifikaciju i računam pouzdanost...' },
  { delay: 16000, message: '✨ Pripremam konačan odgovor...' },
];

interface PdfExportInput {
  hs_code?: string;
  description?: string;
  confidence?: string;
  confidence_score?: number;
  gir_path?: string[];
  legal_basis?: string;
  reasoning?: string;
  caseId?: string;
}

interface PdfSections {  header: { title: string; subtitle: string; date: string; reportId: string };
  classificationDetails: { 
    hsCode: string; 
    productDescription: string; 
    confidence: string; 
    dutyRate: number | null; 
    notes: string;
    reasoning?: string;
    citations?: string[];
  };
  legalBasis: { girPath: string[]; legalText: string; girSteps?: GirStep[] };
  // V2.4: Legal consequences section (backend-driven)
  legalConsequences?: LegalConsequence[];
  // V2.4: CN measures section (backend-driven)
  cnMeasures?: CnMeasuresSection | null;
  auditTrail: { exportedAt: string; exportedBy: string; exportFormat: string; dataSource?: string };
}

// V2.4: Legal consequence type
interface LegalConsequence {
  severity: string;
  category: string;
  message: string;
  actionRequired: string | null;
  certificateCodes: string[];
  applicableOrigins: string[];
}

// V2.4: GIR step type
interface GirStep {
  stepOrder: number;
  girRuleCode: string | null;
  applied: boolean;
  outcome: string;
  rationale: string;
}

// V2.4: CN measures section type
interface CnMeasuresSection {
  confirmed: Array<{
    measureType: string;
    measureTypeCode: string;
    duty: string;
    originCode: string;
    originName: string | null;
    legalBase: string;
  }>;
  conditional: Array<{
    measureType: string;
    measureTypeCode: string;
    duty: string;
    originCode: string;
    requires: string;
  }>;
  completeness: {
    status: string;
    missingData: string[];
  };
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Convert a File to base64 string
 */
function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove data URL prefix (e.g., "data:image/jpeg;base64,")
      const base64 = result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

interface ChatProps {
  lang?: SupportedLanguage;
}

// Chat renders the canonical backend status. A mixed-version response without
// status fails closed as NEEDS_INFO; the frontend never derives FINAL from score.

export function Chat({ lang = 'bs' }: ChatProps) {
  const authUser = useAuthUser();
  const { isAuthenticated, isLoading: convexAuthLoading } = useConvexAuth();
  const canonicalUserId = authUser?.id;
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [originalDescription, setOriginalDescription] = useState<string>('');
  const [clarificationContext, setClarificationContext] = useState<string>('');
  const [pendingClarification, setPendingClarification] = useState(false);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [progressMessage, setProgressMessage] = useState(PROGRESS_STEPS[0].message);
  const [linkDetected, setLinkDetected] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historySearch, setHistorySearch] = useState('');
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  const chat = useAction(api.unifiedAgent.chat);
  const classify = useAction(api.unifiedAgent.classify);
  const generatePdfData = useAction(api.pdfExport.generatePdfData);
  const saveClassification = useMutation(api.classificationHistory.saveClassification);
  const recordInteraction = useMutation(api.sessionLearning.recordInteraction);

  // History queries
  const canQueryHistory = Boolean(canonicalUserId && isAuthenticated && !convexAuthLoading);

  const historyResults = useQuery(
    api.classificationHistory.searchHistory,
    canQueryHistory
      ? { user_id: canonicalUserId, searchTerm: historySearch || undefined }
      : 'skip'
  );

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isLoading]);

  // Cleanup image preview URL on unmount
  useEffect(() => {
    return () => {
      if (imagePreviewUrl) {
        URL.revokeObjectURL(imagePreviewUrl);
      }
    };
  }, [imagePreviewUrl]);

  // Progress message cycling during loading
  useEffect(() => {
    if (!isLoading) {
      setProgressMessage(PROGRESS_STEPS[0].message);
      return;
    }
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      let current = PROGRESS_STEPS[0].message;
      for (const step of PROGRESS_STEPS) {
        if (elapsed >= step.delay) current = step.message;
      }
      setProgressMessage(current);
    }, 500);
    return () => clearInterval(interval);
  }, [isLoading]);

  // Auto-resize textarea to fit content
  const autoResizeTextarea = useCallback(() => {
    const ta = textareaRef.current;
    if (!ta) return;
    ta.style.height = 'auto';
    const maxH = 200; // max ~8 lines
    ta.style.height = Math.min(ta.scrollHeight, maxH) + 'px';
    ta.style.overflowY = ta.scrollHeight > maxH ? 'auto' : 'hidden';
  }, []);

  // Detect URLs in input
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setInput(val);
    setLinkDetected(/https?:\/\/\S+/.test(val.trim()));
    // resize on next frame so value is committed
    requestAnimationFrame(autoResizeTextarea);
  }, [autoResizeTextarea]);

  // Enter = send, Shift+Enter = new line
  const handleTextareaKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (input.trim() && !isLoading) {
        const form = e.currentTarget.closest('form');
        if (form) form.requestSubmit();
      }
    }
  }, [input, isLoading]);

  // Handle paste — allow multi-line text and auto-resize
  const handlePaste = useCallback((_e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    // Paste is handled natively by textarea; just trigger resize after
    requestAnimationFrame(autoResizeTextarea);
  }, [autoResizeTextarea]);

  const processImageFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      alert(t('imageSelectError', lang));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert(t('imageSizeError', lang));
      return;
    }
    setSelectedImage(file);
    const previewUrl = URL.createObjectURL(file);
    setImagePreviewUrl(previewUrl);
  }, [lang]);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) processImageFile(file);
  };

  const removeSelectedImage = () => {
    setSelectedImage(null);
    if (imagePreviewUrl) {
      URL.revokeObjectURL(imagePreviewUrl);
      setImagePreviewUrl(null);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Drag and drop handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processImageFile(file);
  }, [processImageFile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    if (!canonicalUserId) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: t('errorGeneric', lang),
      }]);
      return;
    }

    const userMessage = input.trim();
    setInput('');
    setLinkDetected(false);
    // Reset textarea height after send
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    
    // Collect conversation history BEFORE adding current message
    const conversationHistory = messages
      .map(msg => `${msg.role === 'user' ? 'Korisnik' : 'Asistent'}: ${msg.content}`)
      .join('\n');
    
    // Store image preview for the message
    const messageImagePreview = imagePreviewUrl || undefined;
    
    setMessages(prev => [...prev, { role: 'user', content: userMessage, imagePreview: messageImagePreview }]);
    setIsLoading(true);

    let description = userMessage;
    let additionalInfo = '';

    try {

      if (pendingClarification && originalDescription) {
        // Follow-up turn while clarification is pending:
        // preserve the original description so the backend lock/sanitizer still fires.
        description = originalDescription;
        additionalInfo = clarificationContext
          ? (clarificationContext + '\n\nDodatni odgovor: ' + userMessage)
          : userMessage;
        setClarificationContext(additionalInfo);
        // Do NOT call setOriginalDescription here — keep the locked description.
      } else {
        setClarificationContext('');
        setOriginalDescription(description);
      }

      // Convert image to base64 if selected
      let imageBase64: string | undefined;
      if (selectedImage) {
        imageBase64 = await fileToBase64(selectedImage);
        additionalInfo = additionalInfo 
          ? `${additionalInfo}\n[Slika proizvoda priložena]`
          : '[Slika proizvoda priložena]';
      }

      // Use conversational chat() action with thread support
      const chatResult = await chat({
        threadId: threadId || undefined,
        prompt: description,
        tenantId: LAUNCH_TENANT_ID,
        userId: canonicalUserId,
        additionalInfo: additionalInfo || undefined,
        imageBase64: imageBase64 || undefined,
        mode: 'auto',
      });
      
      // Store threadId for conversation continuity
      if (chatResult.threadId && !threadId) {
        setThreadId(chatResult.threadId);
      }
      
      // Clear selected image after submission
      removeSelectedImage();
      
      let responseText: string;
      let isResult = false;
      let classificationData: MessageClassificationData | undefined;
      
      // Strip domain routing debug headers from backend response before display
      const cleanContent = stripDebugHeaders(chatResult.response.content || '');

      // Extract classification from chat response
      const classification = chatResult.response.classification;
      const needsClarification = chatResult.response.needsClarification;
      const auditPayload = isChatRuntimePayload(chatResult.response.audit)
        ? chatResult.response.audit
        : undefined;
      
      if (classification && classification.hs_code) {
        const classificationRecord = classification as Record<string, unknown>;
        // We have a classification result (may still require human review based on confidence/needsClarification)
        isResult = true;
        
        const resolvedStatus = classification.status ?? "NEEDS_INFO";
        const resolvedConfidenceScore =
          typeof classification.confidence_score === "number"
            ? normalizeConfidenceScore(classification.confidence_score)
            : 0;

        // Normalize the backend response through the canonical normalizer
        const normalizedClassification = normalizeClassificationResult({
          hs_code: classification.hs_code,
          confidence: classification.confidence,
          confidence_score: resolvedConfidenceScore,
          status: resolvedStatus,
          gir_path: classification.gir_path,
          legal_basis: classification.legal_basis,
          reasoning: cleanContent,
          mode_used: chatResult.modeUsed,
          from_cache: false,
          processing_time_ms: chatResult.processingTimeMs,
          tools_used: chatResult.toolsUsed,
          retrieval_sources: chatResult.retrievalSources,
          caseId: chatResult.caseId,
          validatorGate: classificationRecord.validatorGate ?? auditPayload?.validatorGate,
          validation: classificationRecord.validation ?? auditPayload?.validation,
          missing_info: classificationRecord.missing_info ?? auditPayload?.missing_info,
          suggested_questions:
            classificationRecord.suggested_questions ?? auditPayload?.suggested_questions,
          universalReasoning: classification.universalReasoning,
        });

        responseText = formatResult(normalizedClassification);
        classificationData = {
          ...normalizedClassification,
          description,
        };

        if (isFinalGateEligible(normalizedClassification)) {
          // Save only gate-eligible classifications to classification history.
          try {
            await saveClassification({
              user_id: canonicalUserId,
              hs_code: classification.hs_code,
              description: description,
              confidence: normalizedClassification.confidence,
              confidence_score: normalizedClassification.confidence_score,
              status: normalizedClassification.status,
              reasoning: cleanContent,
              gir_path: classification.gir_path && classification.gir_path.length > 0 ? classification.gir_path : undefined,
              legal_basis: classification.legal_basis || undefined,
              alternatives: undefined,
              mode_used: chatResult.modeUsed,
              from_cache: false,
              processing_time_ms: chatResult.processingTimeMs,
              // V2.4: Link to audit-grade case for replay parity
              caseId: chatResult.caseId as Id<'classification_cases'> | undefined,
            });
          } catch (historyError) {
            console.error('Failed to save classification history:', historyError);
          }

          // Record this interaction for session learning only after the gate allows final output.
          try {
            await recordInteraction({
              session_id: threadId || `session_${Date.now()}`,
              user_id: canonicalUserId,
              input_description: description,
              additional_info: additionalInfo || undefined,
              result_hs_code: classification.hs_code,
              result_confidence: classification.confidence,
              result_reasoning: cleanContent.substring(0, 500),
            });
          } catch (learningError) {
            console.error('Failed to record interaction:', learningError);
          }

          // Reset context after successful gate-eligible classification.
          setOriginalDescription('');
          setClarificationContext('');
          setPendingClarification(false);
        } else {
          setPendingClarification(normalizedClassification.status === "NEEDS_INFO");
          if (normalizedClassification.status === "STOP") {
            setOriginalDescription('');
            setClarificationContext('');
          }
        }
      } else if (auditPayload) {
        isResult = true;
        const normalizedAudit = normalizeClassificationResult({
          ...auditPayload,
          reasoning: cleanContent || String(auditPayload.reasoning ?? ""),
          mode_used: chatResult.modeUsed,
          from_cache: false,
          processing_time_ms: chatResult.processingTimeMs,
          tools_used: auditPayload.tools_used ?? chatResult.toolsUsed,
          retrieval_sources: chatResult.retrievalSources,
          caseId: chatResult.caseId,
        });
        responseText = formatResult(normalizedAudit);
        classificationData = {
          ...normalizedAudit,
          description,
        };
        setPendingClarification(normalizedAudit.status === "NEEDS_INFO" || needsClarification);
        if (normalizedAudit.status === "STOP") {
          setOriginalDescription('');
          setClarificationContext('');
        }
      } else if (needsClarification) {
        // System is asking for more information
        responseText = `💬 ${cleanContent}`;
        setPendingClarification(true);
      } else {
        // Fallback for unexpected response format
        responseText = cleanContent || t('errorClassify', lang);
        setPendingClarification(false);
        setOriginalDescription('');
        setClarificationContext('');
      }
      
      setMessages(prev => [...prev, { role: 'assistant', content: responseText, isResult, classificationData }]);
    } catch (error) {
      console.error('Classification error:', error);
      console.error('Classification context:', { 
        description, 
        additionalInfo, 
        originalDescription,
        clarificationContext 
      });
      setPendingClarification(false);
      setOriginalDescription('');
      setClarificationContext('');
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: t('errorGeneric', lang)
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const formatResult = (result: CanonicalClassificationResult): string => {
    const view = buildPublicClassificationView(result);
    return formatPublicClassificationText(view);
  };

  const handleNewConversation = () => {
    setMessages([]);
    setThreadId(null);
    setOriginalDescription('');
    setClarificationContext('');
    setLinkDetected(false);
    removeSelectedImage();
  };

  const handleExportPdf = async (data: PdfExportInput) => {
    try {
      if (!data.caseId) {
        throw new Error('OFFICIAL_REPORT_REQUIRES_PERSISTED_DECISION');
      }
      const pdfData = await generatePdfData({
        classificationCaseId: data.caseId as Id<'classification_cases'>,
      });

      const sections = (pdfData as { sections: PdfSections }).sections;

      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert(t('popupBlocked', lang));
        return;
      }

      const h = (s: string) => escapeHtml(s);

      printWindow.document.write(`<!DOCTYPE html><html><head><title>${h(sections.header.title)}</title>
<style>
  body {
    font-family: 'Segoe UI', sans-serif;
    padding: 0;
    color: #2e241b;
    margin: 0;
    background: #f4eee2;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .pdf-watermark {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    opacity: 0.11;
    background-image: url('${bihFlagWatermark}');
    background-repeat: no-repeat;
    background-position: right -120px top 80px;
    background-size: 540px;
    filter: blur(9px) saturate(0.9);
  }
  .pdf-overlay {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 1;
    background: linear-gradient(180deg, rgba(244,238,226,0.48) 0%, rgba(244,238,226,0.78) 100%);
  }
  .report-shell {
    position: relative;
    z-index: 2;
    max-width: 820px;
    margin: 28px auto;
    background: rgba(255,255,255,0.95);
    border: 1px solid rgba(125, 99, 71, 0.2);
    border-radius: 14px;
    padding: 34px 36px;
    box-shadow: 0 10px 28px rgba(63, 46, 30, 0.12);
  }
  h1 { color: #1d456e; border-bottom: 3px solid #2e628f; padding-bottom: 10px; }
  h2 { color: #23557f; margin-top: 30px; }
  .field { margin: 10px 0; } .label { font-weight: 600; color: #5a6c7d; }
  .value { margin-left: 8px; } .footer { margin-top: 40px; padding-top: 15px; border-top: 1px solid #e0e5ea; font-size: 0.85rem; color: #9ca3af; }
  .section { margin: 20px 0; padding: 15px; background: #f8f5ef; border-radius: 8px; }
  .reasoning-section { margin: 20px 0; padding: 15px; background: #e8f1f7; border-left: 4px solid #2d5a87; border-radius: 6px; }
  @media print {
    .report-shell {
      margin: 0;
      border-radius: 0;
      border: none;
      box-shadow: none;
      max-width: none;
      min-height: calc(100vh - 20px);
    }
  }
</style></head><body>
  <div class="pdf-watermark"></div>
  <div class="pdf-overlay"></div>
  <div class="report-shell">
  <h1>${h(sections.header.title)}</h1>
  <p>${h(sections.header.subtitle)} — ${h(new Date(sections.header.date).toLocaleDateString('hr-HR'))}</p>
  <h2>Detalji klasifikacije</h2>
  <div class="field"><span class="label">Predloženi HS Code:</span><span class="value">${h(sections.classificationDetails.hsCode)}</span></div>
  <div class="field"><span class="label">Opis:</span><span class="value">${h(sections.classificationDetails.productDescription)}</span></div>
  <div class="field"><span class="label">Pouzdanost:</span><span class="value">${h(sections.classificationDetails.confidence)}</span></div>
  ${sections.classificationDetails.dutyRate != null ? `<div class="field"><span class="label">Stopa carine:</span><span class="value">${h(String(sections.classificationDetails.dutyRate))}%</span></div>` : ''}
  ${sections.classificationDetails.notes ? `<div class="field"><span class="label">Napomene:</span><span class="value">${h(sections.classificationDetails.notes)}</span></div>` : ''}
  ${sections.classificationDetails.reasoning ? `
  <div class="reasoning-section">
    <div class="field"><span class="label">📋 Obrazloženje:</span></div>
    <div class="value" style="margin-top: 8px;">${h(sections.classificationDetails.reasoning)}</div>
  </div>` : ''}
  ${sections.legalBasis.girPath && sections.legalBasis.girPath.length > 0 ? `
  <h2>Pravna osnova</h2>
  <div class="section">
    <div class="field"><span class="label">⚖️ GIR pravila:</span><span class="value">${h(sections.legalBasis.girPath.join(' → '))}</span></div>
    ${sections.legalBasis.legalText ? `<div class="field"><span class="label">📜 Pravna osnova:</span><span class="value">${h(sections.legalBasis.legalText)}</span></div>` : ''}
  </div>` : ''}
  ${sections.classificationDetails.citations && sections.classificationDetails.citations.length > 0 ? `
  <h2>Citati iz nomenklature</h2>
  <div class="section">
    <div class="field"><span class="label">📑 Citati:</span></div>
    <ul style="margin-top: 8px;">
      ${sections.classificationDetails.citations.map((citation: string) => `<li>${h(citation)}</li>`).join('')}
    </ul>
  </div>` : ''}
  ${sections.legalConsequences && sections.legalConsequences.length > 0 ? `
  <h2>⚠️ Pravne posljedice</h2>
  <div class="section">
    ${sections.legalConsequences.map((lc: LegalConsequence) => `
      <div class="legal-consequence ${lc.severity.toLowerCase()}" style="margin-bottom: 12px; padding: 10px; border-left: 4px solid ${lc.severity === 'BLOCKING' ? '#dc2626' : lc.severity === 'WARNING' ? '#f59e0b' : '#3b82f6'}; background: ${lc.severity === 'BLOCKING' ? '#fef2f2' : lc.severity === 'WARNING' ? '#fffbeb' : '#eff6ff'};">
        <div style="font-weight: 600; margin-bottom: 4px;">
          ${lc.severity === 'BLOCKING' ? '🚫' : lc.severity === 'WARNING' ? '⚠️' : 'ℹ️'} 
          [${h(lc.category)}] ${h(lc.message)}
        </div>
        ${lc.actionRequired ? `<div style="font-size: 0.9em; color: #666;">📌 Potrebna akcija: ${h(lc.actionRequired)}</div>` : ''}
        ${lc.certificateCodes.length > 0 ? `<div style="font-size: 0.9em; color: #666;">📜 Certifikati: ${h(lc.certificateCodes.join(', '))}</div>` : ''}
      </div>
    `).join('')}
  </div>` : ''}
  ${sections.cnMeasures && sections.cnMeasures.confirmed.length > 0 ? `
  <h2>📊 Trgovinske mjere</h2>
  <div class="section">
    <table style="width: 100%; border-collapse: collapse; font-size: 0.9em;">
      <thead>
        <tr style="background: #f3f4f6;">
          <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Tip</th>
          <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Carina</th>
          <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Porijeklo</th>
          <th style="padding: 8px; border: 1px solid #ddd; text-align: left;">Pravna osnova</th>
        </tr>
      </thead>
      <tbody>
        ${sections.cnMeasures.confirmed.map((m: { measureType: string; duty: string; originCode: string; originName: string | null; legalBase: string }) => `
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd;">${h(m.measureType === 'import' ? 'Uvoz' : 'Izvoz')}</td>
            <td style="padding: 8px; border: 1px solid #ddd;">${h(m.duty)}</td>
            <td style="padding: 8px; border: 1px solid #ddd;">${h(m.originName || m.originCode)}</td>
            <td style="padding: 8px; border: 1px solid #ddd;">${h(m.legalBase)}</td>
          </tr>
        `).join('')}
      </tbody>
    </table>
    ${sections.cnMeasures.completeness.status !== 'COMPLETE' ? `
      <div style="margin-top: 8px; padding: 8px; background: #fffbeb; border: 1px solid #f59e0b; border-radius: 4px;">
        ⚠️ Podaci: ${h(sections.cnMeasures.completeness.status)}
        ${sections.cnMeasures.completeness.missingData.length > 0 ? ` — Nedostaje: ${h(sections.cnMeasures.completeness.missingData.join(', '))}` : ''}
      </div>
    ` : ''}
  </div>` : ''}
  <div class="footer">
    <p>Report ID: ${h(sections.header.reportId)}</p>
    <p>Exported: ${h(sections.auditTrail.exportedAt)}</p>
    ${sections.auditTrail.dataSource === 'BACKEND' ? '<p style="color: green;">✅ Izvor podataka: Backend (audit-grade)</p>' : ''}
  </div>
  </div>
</body></html>`);
      printWindow.document.close();
      printWindow.print();
    } catch (error) {
      console.error('PDF export error:', error);
    }
  };

  return (
    <div className="chat-layout">
      {/* History Sidebar */}
      <div className={`history-sidebar ${historyOpen ? 'open' : ''}`} data-testid="history-sidebar">
        <div className="history-header">
          <h3>📋 {t('history', lang)}</h3>
          <div className="history-header-actions">
            <button 
              className="history-clear-btn" 
              onClick={handleNewConversation} 
              title='Očisti historiju'
            >
              🗑️
            </button>
            <button className="history-close-btn" onClick={() => setHistoryOpen(false)}>✕</button>
          </div>
        </div>
        <div className="history-search">
          <input
            data-testid="history-search-input"
            type="text"
            value={historySearch}
            onChange={(e) => setHistorySearch(e.target.value)}
            placeholder={t('searchHistory', lang)}
          />
        </div>
        <div className="history-list" data-testid="history-list">
          {historyResults && historyResults.length > 0 ? (
            (historyResults as HistoryItem[]).map((item: HistoryItem) => (
              <HistoryItemReplay
                key={item._id}
                item={item}
                locale={LOCALE_MAP[lang] || 'hr-HR'}
                exportLabel="Official verified report unavailable"
                onExportPdf={() => handleExportPdf({
                  hs_code: item.hs_code,
                  description: item.description,
                  confidence: item.confidence,
                  confidence_score: item.confidence_score,
                  gir_path: item.gir_path || [],
                  legal_basis: item.legal_basis || '',
                  reasoning: item.reasoning || '',
                  // V2.4: Pass caseId for backend-driven PDF
                  caseId: item.caseId,
                })}
              />
            ))
          ) : (
            <p className="history-empty">{t('noHistory', lang)}</p>
          )}
        </div>
      </div>

      <div
        className={`chat-container ${isDragOver ? 'drag-over' : ''}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Drag overlay */}
        {isDragOver && (
          <div className="drag-overlay">
            <div className="drag-overlay-content">
              <span className="drag-overlay-icon">📷</span>
              <span>{t('dragDropActive', lang)}</span>
            </div>
          </div>
        )}

        <div className="chat-header">
          <h1>{t('chatTitle', lang)}</h1>
          <p>{t('chatSubtitle', lang)}</p>
          <div className="chat-header-actions">
            <button 
              className="help-toggle-btn" 
              onClick={() => setHelpModalOpen(true)} 
              title='Pomoć'
            >
              ❓
            </button>
            <button className="history-toggle-btn" data-testid="history-toggle" onClick={() => setHistoryOpen(!historyOpen)} title={t('history', lang)}>
              📋
            </button>
            {messages.length > 0 && (
              <button className="new-chat-btn" onClick={handleNewConversation}>
                {t('newClassification', lang)}
              </button>
            )}
          </div>
        </div>
        
        <div className="messages">
          {messages.length === 0 && (
            <div className="welcome-message">
              <h2>{t('welcomeTitle', lang)}</h2>
              <p>{t('welcomeMessage', lang)}</p>
              <p>{t('welcomeImageHint', lang)}</p>
              <p className="drag-drop-hint">{t('dragDropHint', lang)}</p>
              <div className="example">
                <p>{t('welcomeExample', lang)}</p>
              </div>
            </div>
          )}
          
          {messages.map((msg, idx) => (
            <div key={idx} className={`message ${msg.role}`} data-testid={msg.isResult ? 'classification-result' : msg.role === 'assistant' ? 'assistant-message' : 'user-message'}>
              <div className={`message-content ${msg.isResult ? 'result' : ''}`}>
                {msg.imagePreview && (
                  <div className="message-image-preview">
                    <img src={msg.imagePreview} alt="Priložena slika proizvoda" />
                  </div>
                )}
                {msg.content.split('\n').map((line, i) => (
                  <p key={i}>{line}</p>
                ))}

              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="message assistant">
              <div className="message-content loading">
                <TypingIndicator message={progressMessage} />
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Image preview bar */}
        {imagePreviewUrl && (
          <div className="image-preview-bar">
            <img src={imagePreviewUrl} alt="Odabrana slika" className="image-preview-thumb" />
            <span className="image-preview-name">{selectedImage?.name}</span>
            <button className="image-preview-remove" onClick={removeSelectedImage} title="Ukloni sliku">
              ✕
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="input-form">
          <input
            type="file"
            ref={fileInputRef}
            accept="image/jpeg,image/png,image/webp"
            onChange={handleImageSelect}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            className="image-upload-btn"
            onClick={() => fileInputRef.current?.click()}
            disabled={isLoading}
            title="Priloži sliku proizvoda"
          >
            📷
          </button>
          <div className="input-wrapper">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={handleInputChange}
              onKeyDown={handleTextareaKeyDown}
              onPaste={handlePaste}
              placeholder={originalDescription ? t('inputPlaceholderAdditional', lang) : t('inputPlaceholder', lang)}
              disabled={isLoading}
              autoFocus
              rows={1}
              data-testid="product-input"
            />
            {linkDetected && (
              <div className="link-detected-indicator">
                {t('linkDetected', lang)}
              </div>
            )}
          </div>
          <button type="submit" disabled={isLoading || !input.trim()} data-testid="classify-button">
            <span>{t('sendButton', lang)}</span>
          </button>
        </form>
      </div>
      
      {/* Help Modal */}
      <HelpModal 
        isOpen={helpModalOpen} 
        onClose={() => setHelpModalOpen(false)} 
        lang={lang} 
      />
    </div>
  );
}


